import { renderFaceParts } from './parts/FaceParts';
import { socketsFor } from './parts/eyes/EyeStyles';

import { DEFAULT_GHOST } from './AppearanceDefinitions';
import type { ShapeType } from './AppearanceDefinitions';

import { GearParts } from './parts/GearParts';
import { WeaponParts } from './parts/WeaponParts';
import type { WeaponPose } from './parts/WeaponParts';
import type { ResolvedPlayerEquipmentVisuals } from '../../shared/equipmentVisuals';
import { poseTypeForFamily } from '../../shared/equipmentVisuals';
import { attachmentAnchorsForPose } from './parts/AttachmentGeometry';
import type { GhostPoseAnchors } from './parts/AttachmentGeometry';
import {
  BODY_PATHS,
  BODY_RIGS,
  HUMANOID_GEOMETRY,
  bodyScaleFor,
  normalizeBodySize,
} from './parts/HumanoidGeometry';
import { svgGradientDef, svgShadow } from './svg/SVGUtils';
import { createCircle, createPath, darken } from './parts/humanoid/svgPrims';
import { EMPTY_LAYERS, type LayeredContent } from './parts/humanoid/HumanoidExtras';
// Hush wardrobe wrapper: identical to renderHumanoidExtras unless `humanoid.hushWardrobe` is set.
import { renderHumanoidExtrasWithHush as renderHumanoidExtras } from './hushWardrobe/adapter';
import { NPC_RIM_FILTER_ID, bustBackdrop, bustFrame, finishPainter, rimFilterDef } from './parts/humanoid/NpcFinish';
import type { HumanoidAppearance, IdlePhase } from './parts/humanoid/HumanoidTypes';

export type {
  BeardConfig, CloakConfig, GarmentConfig, HairConfig, HeadgearConfig, HumanoidAppearance, HumanoidExtras,
  IdlePhase, NpcArtProfile, NpcLight, NpcProp, NpcRole,
} from './parts/humanoid/HumanoidTypes';

export type GhostPose = WeaponPose;

export interface GhostRenderOptions {
  pose?: GhostPose;
  /** Secondary-motion frame for NPC cloth/props (plan 12); ignored by plain appearances. */
  idlePhase?: IdlePhase;
  /** 'bust' crops to head and shoulders for portraits — only for NPCs with an art profile. */
  frame?: 'full' | 'bust';
}

/**
 * Hair and headgear are authored on the classic head (x 28–96, top y 7). With
 * `humanoid.fit` they are mapped onto this body's head landmarks: full width,
 * gentler height, so a hat sits on a wide dome instead of perching on it.
 */
function headFitTransform(rig: (typeof BODY_RIGS)[ShapeType]): string {
  const classic = BODY_RIGS.classic.head;
  const sx = (rig.head.outerRight - rig.head.outerLeft) / (classic.outerRight - classic.outerLeft);
  const sy = 1 + (sx - 1) * 0.5;
  const cx = (rig.head.outerLeft + rig.head.outerRight) / 2;
  const classicCx = (classic.outerLeft + classic.outerRight) / 2;
  const f = (v: number) => Number(v.toFixed(3));
  return `translate(${f(cx)} ${rig.head.topY}) scale(${f(sx)} ${f(sy)}) translate(${-classicCx} ${-classic.topY})`;
}

/** Readable facial ink for both pale spirits and dark rogue/warlock bodies. */
function normalizeRenderSize(size: number): number {
  if (!Number.isFinite(size)) return 200;
  return Math.max(16, Math.min(1024, Math.round(size)));
}

interface PoseLayers {
  back: string;
  feet: string;
  front: string;
  accent: string;
  anchors: GhostPoseAnchors;
}

function renderPoseLayers(
  color: string,
  pose: GhostPose,
  weaponType?: NonNullable<HumanoidAppearance['weapon']>['type'],
  shape: ShapeType = 'classic',
): PoseLayers {
  const outline = darken(color, 0.52);
  const shade = darken(color, 0.78);
  const rig = BODY_RIGS[shape];
  const anchors = attachmentAnchorsForPose(pose, weaponType, shape);
  const { backHand, frontHand, backFoot, frontFoot } = anchors;
  const { shoulders } = rig.attachments;
  let accent = '';

  switch (pose) {
  case 'walkA':
    break;
  case 'walkB':
    break;
  case 'attack':
    accent = `<g data-direction-cue="attack-forward" fill="none" stroke="#ffffff" stroke-linecap="round">
        <path d="M108,31 Q132,44 121,70" stroke-width="2.4" opacity="0.38" />
        <path d="M116,29 L127,34" stroke-width="1.5" opacity="0.28" />
      </g>`;
    break;
  case 'cast':
    accent = `<g data-direction-cue="cast-forward" fill="none" stroke="#bfe7ff" stroke-linecap="round">
        <circle cx="115" cy="45" r="8" stroke-width="1.5" opacity="0.62" />
        <circle cx="115" cy="45" r="12" stroke-width="1" stroke-dasharray="4 4" opacity="0.48" />
        <path d="M103,43 Q116,31 129,40 M104,52 Q117,60 130,52" stroke-width="1.8" opacity="0.58" />
        <path d="M124,35 L131,39 M125,57 L132,53" stroke-width="1.4" opacity="0.42" />
      </g>`;
    break;
  case 'hurt':
    accent = `<g data-direction-cue="hurt-recoil" stroke="#ffd8c7" stroke-linecap="round" opacity="0.5">
        <path d="M28,43 L20,39 M29,51 L20,51 M31,59 L22,64" stroke-width="2" />
      </g>`;
    break;
  case 'jump':
    accent = `<g data-direction-cue="jump-forward" fill="none" stroke="#d8f3ff" stroke-linecap="round" opacity="0.32">
        <path d="M29,91 L14,96 M35,99 L20,107" stroke-width="2" />
      </g>`;
    break;
  case 'fall':
    accent = `<g data-direction-cue="fall-forward" fill="none" stroke="#d8f3ff" stroke-linecap="round" opacity="0.28">
        <path d="M24,31 L12,24 M29,38 L16,34" stroke-width="2" />
      </g>`;
    break;
  case 'death':
    break;
  case 'idle':
  case 'blink':
    break;
  }

  const arm = (fromX: number, fromY: number, handX: number, handY: number, width: number) =>
    `<path d="M${fromX},${fromY} Q${(fromX + handX) / 2},${(fromY + handY) / 2 + 3} ${handX},${handY}" fill="none" stroke="${shade}" stroke-width="${width}" stroke-linecap="round" />`;
  const hand = (name: 'back-hand' | 'front-hand', x: number, y: number, r: number) =>
    `<circle data-body-anchor="${name}" data-anchor-x="${x}" data-anchor-y="${y}" cx="${x}" cy="${y}" r="${r}" fill="${color}" stroke="${outline}" stroke-width="1.2" />`;
  const foot = (name: 'back-foot' | 'front-foot', x: number, y: number, angle: number, opacity: number) =>
    `<path data-body-anchor="${name}" data-anchor-x="${x}" data-anchor-y="${y}" d="M${x - 11},${y - 5} Q${x},${y - 10} ${x + 13},${y - 2} L${x + 10},${y + 5} L${x - 10},${y + 5} Z" fill="${shade}" stroke="${outline}" stroke-width="1.2" opacity="${opacity}" transform="rotate(${angle} ${x} ${y})" />`;

  return {
    back: `${arm(shoulders.back.x, shoulders.back.y, backHand.x, backHand.y, 9)}${hand('back-hand', backHand.x, backHand.y, 5)}`,
    feet: `${foot('back-foot', backFoot.x, backFoot.y, backFoot.angle, 0.8)}${foot('front-foot', frontFoot.x, frontFoot.y, frontFoot.angle, 1)}`,
    front: `${arm(shoulders.front.x, shoulders.front.y, frontHand.x, frontHand.y, 10)}${hand('front-hand', frontHand.x, frontHand.y, 5.5)}`,
    accent,
    anchors,
  };
}

function renderAura(aura: HumanoidAppearance['aura']): string {
  switch (aura) {
  case 'flame':
    return `<g fill="none" stroke="#ff8a45" opacity="0.42" stroke-linecap="round">
        <path d="M25,102 Q12,88 24,71 M103,101 Q121,84 109,66" stroke-width="3" />
        <path d="M34,111 Q19,101 29,89 M96,110 Q112,99 103,84" stroke-width="1.6" />
      </g>`;
  case 'frost':
    return `<g fill="#bfeaff" opacity="0.5">
        <path d="M22,81 l4,-7 l4,7 l-4,7 Z" /><path d="M109,72 l4,-7 l4,7 l-4,7 Z" />
        <path d="M24,104 l3,-5 l3,5 l-3,5 Z" /><path d="M104,103 l3,-5 l3,5 l-3,5 Z" />
      </g>`;
  case 'shadow':
    return `<g fill="none" stroke="#9a67d6" opacity="0.34">
        <ellipse cx="66" cy="78" rx="57" ry="40" stroke-width="3" stroke-dasharray="9 7" />
        <ellipse cx="66" cy="82" rx="49" ry="33" stroke-width="1.4" />
      </g>`;
  case 'holy':
    return `<g fill="none" stroke="#ffe9a3" opacity="0.55">
        <ellipse cx="66" cy="13" rx="25" ry="7" stroke-width="3" />
        <path d="M18,59 H7 M121,59 H132 M66,4 V-4" stroke-width="2" />
      </g>`;
  case 'none':
  default:
    return '';
  }
}

function renderAccessory(accessory: HumanoidAppearance['accessory'], eyeStyle?: HumanoidAppearance['eyeStyle']): string {
  switch (accessory) {
  case 'bow':
    return '<path d="M55,12 Q66,1 77,12 Q66,17 55,12 Z M77,12 Q88,1 96,15 Q85,18 77,12 Z" fill="#c94d70" stroke="#6f263f" stroke-width="1.2" />';
  case 'hat':
    return '<path d="M39,22 Q42,2 66,2 Q88,4 91,24 Z" fill="#493760" stroke="#241b31" stroke-width="1.5" /><ellipse cx="67" cy="23" rx="39" ry="7" fill="#342744" />';
  case 'crown':
    return '<path d="M43,23 L39,5 L52,15 L64,1 L75,15 L91,5 L87,25 Z" fill="#e5b83b" stroke="#745713" stroke-width="1.5" />';
  case 'glasses':
    if (eyeStyle && eyeStyle.style !== 'profile') {
      // Library eyes wear matching lenses: rims sit around the eyes, never magnify them.
      const { far, near } = socketsFor(eyeStyle);
      const r = Number((far.rx + 2.8).toFixed(2));
      return `<g data-face-glasses="matching" fill="none" stroke="#272b32" stroke-width="1.8"><circle cx="${far.x}" cy="${far.y}" r="${r}" /><circle cx="${near.x}" cy="${near.y}" r="${r}" /><path d="M${far.x + r},${far.y - 1} Q${(far.x + near.x) / 2},${far.y - 4} ${near.x - r},${near.y - 1} M${far.x - r},${far.y - 1} L${far.x - r - 8},${far.y - 4} M${near.x + r},${near.y - 1} L${near.x + r + 8},${near.y - 4}" /></g>`;
    }
    return '<g fill="none" stroke="#272b32" stroke-width="2"><circle cx="49" cy="44" r="10" /><circle cx="76" cy="43" r="11" /><path d="M59,43 L65,43 M39,42 L31,39 M87,41 L94,38" /></g>';
  case 'horns':
    return '<path d="M39,27 Q20,17 27,2 Q36,11 47,18 Z M84,20 Q101,7 109,20 Q98,17 90,31 Z" fill="#c8b79c" stroke="#655b4b" stroke-width="1.5" />';
  case 'halo':
    return '<ellipse cx="67" cy="7" rx="27" ry="6" fill="none" stroke="#fff0a8" stroke-width="3" opacity="0.85" />';
  case 'bandana':
    return '<path d="M33,31 Q63,22 91,30 L89,38 Q62,31 34,39 Z" fill="#9f3041" /><path d="M34,35 L20,43 L31,48 Z" fill="#7e2434" />';
  case 'none':
  default:
    return '';
  }
}

function renderTrail(trail: HumanoidAppearance['trail']): string {
  if (!trail || trail === 'none') return '';
  const color = trail === 'sparkles' ? '#fff3a6' : trail === 'petals' ? '#e8a5bd' : '#9aa4b3';
  return `<g fill="${color}" opacity="0.38">
    <circle cx="17" cy="79" r="3" /><circle cx="8" cy="92" r="2" /><circle cx="25" cy="106" r="2.5" />
  </g>`;
}

export class GhostRenderer {

  static render(props: HumanoidAppearance, size: number = 200, options: GhostRenderOptions = {}): string {
    const {
      color, eyes, mouth, shape, faceMarking, blush,
      weapon, equipment, glow, accessory, aura, trail,
      bodySize, opacity, humanoid, equipmentVisuals, footless
    } = { ...DEFAULT_GHOST, humanoid: undefined, ...props };
    const pose = options.pose ?? 'idle';
    const outputSize = normalizeRenderSize(size);

    // --- 1. Base Transforms & Opacity ---
    // Apply authored body size once around the canonical character pivot.
    const resolvedBodySize = normalizeBodySize(bodySize);
    const scale = bodyScaleFor(resolvedBodySize);
    const { characterPivot, viewBox } = HUMANOID_GEOMETRY;
    const mainTransform = `translate(${characterPivot.x} ${characterPivot.y}) scale(${scale}) translate(-${characterPivot.x} -${characterPivot.y})`;

    let globalOpacity = 1;
    if (opacity === 'ghostly') globalOpacity = 0.85;
    if (opacity === 'ethereal') globalOpacity = 0.6;

    // --- 2. Body Shape ---
    const resolvedShape = shape ?? 'classic';
    const bodyPath = BODY_PATHS[resolvedShape];
    const bodyRig = BODY_RIGS[resolvedShape];
    const faceTransform = `translate(${bodyRig.faceOffsetX} 0)`;

    // Humanoid-extras renders get the Coil style-guide shading upgrade:
    // body gradient (base → darker hem) + a soft ground shadow. Plain
    // appearances keep the legacy flat fill.
    const useShading = !!humanoid;
    const metrics = bodyRig.silhouette;
    // Plan 12: an art profile adds layered cloth shading + the home-light rim.
    const art = humanoid?.art;
    const painter = art ? finishPainter(art.light) : undefined;
    // Plan 12 options: `humanoid.fit` cuts cloth to the body outline (see HumanoidExtras).
    const fitted = !!humanoid?.fit;
    const bodyFill = useShading ? 'url(#h2kBodyGrad)' : color;
    const groundShadow = useShading
      ? svgShadow(metrics.centerX, 113, (metrics.right - metrics.left) * 0.4, 4.5, 0.18)
      : '';

    const bodyContent = `${createPath(bodyPath, bodyFill, darken(color, 0.48), 2.2)
      .replace('<path ', '<path data-base-body="true" ')}
      <path data-base-body-highlight="true" d="M87,27 Q103,43 96,87" fill="none" stroke="#ffffff" stroke-width="2.4" opacity="0.2" stroke-linecap="round" />`;

    // --- 2b. Mode decision (BIG-EQUIP-VIS-01 S2 correction) ---
    // The mode must be decided BEFORE poseLayers so legacy weapon?.type
    // cannot affect anchors or pose geometry in authoritative mode.
    // In authoritative mode, the pose/anchor weapon type is derived from
    // the resolved mainHand descriptor's family. If mainHand is unresolved
    // or empty, a neutral deterministic pose is used (no weapon type
    // influence). Legacy weapon/equipment never affect any rendered byte,
    // pose, anchor, geometry, marker, or hash in authoritative mode.
    const authoritativeMode = !!equipmentVisuals;
    let authoritativeWeaponType: NonNullable<HumanoidAppearance['weapon']>['type'] | undefined;
    if (authoritativeMode) {
      const mainHand = (equipmentVisuals as ResolvedPlayerEquipmentVisuals).mainHand;
      if (mainHand.kind === 'resolved') {
        authoritativeWeaponType = poseTypeForFamily(mainHand.descriptor.family);
      }
      // If mainHand is empty/unknown/missing/mismatch, authoritativeWeaponType
      // remains undefined → neutral pose (no weapon-specific anchors).
    } else {
      authoritativeWeaponType = weapon?.type;
    }
    const poseLayers = renderPoseLayers(color, pose, authoritativeWeaponType, resolvedShape);

    // --- 3. Face (Eyes & Mouth) ---
    const { eyes: eyesContent, nose: noseContent, mouth: mouthContent } = renderFaceParts(color, eyes, mouth, pose, { ghostEyes: props.ghostEyes ?? !!humanoid?.art, eyeStyle: props.eyeStyle });

    // --- 4. Markings & Blush ---
    let markingsContent = '';
    if (blush) {
      markingsContent += `<g fill="#ffb6c1" opacity="0.6">
            ${createCircle(37, 56, 5, '#ffb6c1')}
            ${createCircle(91, 53, 6, '#ffb6c1')}
        </g>`;
    }
    if (faceMarking) {
      switch(faceMarking) {
      case 'scar': markingsContent += '<path d="M68,29 L78,58" stroke="#8b0000" stroke-width="2" opacity="0.7" />'; break;
      case 'tattoo': markingsContent += '<path d="M79,32 Q91,24 98,37" stroke="#4b0082" stroke-width="2" fill="none" />'; break;
      case 'freckles': markingsContent += `<g fill="#d2691e" opacity="0.8">
                ${createCircle(57, 54, 1, '#d2691e')}${createCircle(63, 55, 1, '#d2691e')}
                ${createCircle(76, 53, 1, '#d2691e')}${createCircle(83, 52, 1, '#d2691e')}</g>`; break;
      }
    }

    // --- 5. Equipment & Weapons ---
    // Two explicit modes (BIG-EQUIP-VIS-01 S2):
    //  - equipmentVisuals present: render only resolved item descriptors.
    //    Ignore legacy appearance.weapon and appearance.equipment completely,
    //    including DEFAULT_GHOST's default sword and gear.
    //  - equipmentVisuals absent: preserve the existing legacy weapon/equipment
    //    rendering used by NPCs, appearance presets, character previews.
    //
    // The mode decision and pose/anchor derivation already happened above
    // (section 2b) so legacy weapon?.type cannot leak into authoritative mode.
    let gear: { back: string; feet: string; backArm: string; backHand: string; body: string; frontArm: string; frontHand: string; head: string };
    let weaponContent: string;

    if (authoritativeMode) {
      gear = GearParts.renderResolvedLayers(
        equipmentVisuals as ResolvedPlayerEquipmentVisuals,
        poseLayers.anchors,
        { shape: resolvedShape, bodySize: resolvedBodySize },
      );
      weaponContent = WeaponParts.renderResolved(
        (equipmentVisuals as ResolvedPlayerEquipmentVisuals).mainHand,
        pose,
        poseLayers.anchors.frontHand,
        poseLayers.anchors.backHand,
      );
    } else {
      gear = equipment
        ? GearParts.renderLayers(equipment, poseLayers.anchors, {
          shape: resolvedShape,
          bodySize: resolvedBodySize,
        })
        : {
          back: '',
          feet: '',
          backArm: '',
          backHand: '',
          body: '',
          frontArm: '',
          frontHand: '',
          head: '',
        };
      weaponContent = weapon
        ? WeaponParts.render(
          weapon,
          pose,
          poseLayers.anchors.frontHand,
          poseLayers.anchors.backHand,
        )
        : '';
    }

    // Held shields and crossbows must remain visible outside headwear.
    // Only the ranged attack raises the existing supporting arm over the body.
    const heldFamily = equipmentVisuals?.mainHand.kind === 'resolved'
      ? equipmentVisuals.mainHand.descriptor.family : undefined;
    const heldInForeground = heldFamily === 'shield' || heldFamily === 'crossbow';
    const rearInForeground = heldFamily === 'crossbow' && pose === 'attack';
    const rearArmAndHand = `${poseLayers.back}${gear.backArm}${gear.backHand}`;
    const frontArmAndHand = `${poseLayers.front}${gear.frontArm}${gear.frontHand}`;

    // --- 6. Humanoid extras (Coil NPC portraits) ---
    const extras: LayeredContent = humanoid
      ? renderHumanoidExtras(humanoid, resolvedShape, {
        painter, phase: options.idlePhase, frontHand: poseLayers.anchors.frontHand, backHand: poseLayers.anchors.backHand, alignToShape: !!art, fit: fitted,
      })
      : EMPTY_LAYERS;
    const defs = useShading
      ? `<defs>${svgGradientDef('h2kBodyGrad', color, darken(color, 0.82))}${painter?.defs() ?? ''}${art ? rimFilterDef(art.light) : ''}${fitted ? `<clipPath id="h2kBodyClip"><path d="${bodyPath}" /></clipPath>` : ''}</defs>`
      : '';
    const bust = art && options.frame === 'bust' ? bustFrame(scale) : null;
    const box = bust ? `${bust.x} ${bust.y} ${bust.width} ${bust.height}` : `0 0 ${viewBox.width} ${viewBox.height}`;
    // Art NPCs wear hair/hats on the face rig so they sit on non-classic heads.
    const headExtras = fitted
      ? `<g data-npc-fit="head" transform="${headFitTransform(bodyRig)}">${extras.head}</g>`
      : art ? `<g transform="${faceTransform}">${extras.head}</g>` : extras.head;

    // --- Assemble SVG ---
    const style = glow ? `style="filter: drop-shadow(0 0 8px ${color})"` : '';
    const poseTransforms: Partial<Record<GhostPose, string>> = {
      walkA: 'translate(1 0) rotate(1 70 76)',
      walkB: 'translate(1 0) rotate(0.5 70 76)',
      attack: 'rotate(2 70 76)',
      cast: 'translate(1 -1) rotate(1.5 70 76)',
      hurt: 'rotate(-4 70 76)',
      jump: 'translate(2 -2) rotate(2.5 70 76)',
      fall: 'translate(3 1) rotate(3 70 76)',
    };
    const poseTransform = poseTransforms[pose] ?? '';

    return `<svg width="${outputSize}" height="${outputSize}" viewBox="${box}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" data-facing-profile="right" data-pose="${pose}" data-render-mode="${authoritativeMode ? 'equipment-visuals' : 'legacy'}"${footless ? ' data-ghost-feet="none"' : ''}${art ? ` data-npc-art="${art.role}" data-npc-light="${art.light}"` : ''}${bust ? ' data-frame="bust"' : ''} ${style}>
      ${defs}
      ${bust && art ? bustBackdrop(bust, art.light) : ''}
      <g data-character-shape="${resolvedShape}" data-character-size="${resolvedBodySize}" data-character-scale="${scale}" transform="${mainTransform}" opacity="${globalOpacity}">
        ${renderTrail(trail)}
        ${renderAura(aura)}
        ${groundShadow}
        <g transform="${poseTransform}"${art ? ` filter="url(#${NPC_RIM_FILTER_ID})"` : ''}>
        ${footless ? '' : `${poseLayers.feet}${gear.feet}`} <!-- Feet + boots on animated foot anchors; footless = the body's hem is the bottom -->
        ${rearInForeground ? '' : rearArmAndHand}
        ${gear.back} <!-- Equipment capes / mantles -->
        ${extras.back} <!-- Cloak drape / slung weapons -->
        ${bodyContent}
        ${gear.body} <!-- Chest / shoulders / belt -->
        ${fitted ? `<g data-npc-fit="body" clip-path="url(#h2kBodyClip)">${extras.clothing}</g>` : extras.clothing} <!-- Garments / mantles -->
        ${extras.worn}
        ${heldInForeground ? '' : weaponContent}
        ${heldInForeground ? '' : frontArmAndHand}
        <g data-face-rig="${resolvedShape}" transform="${faceTransform}">
          ${eyesContent}
          ${noseContent}
          ${extras.beard}
          ${mouthContent}
          ${markingsContent}
        </g>
        ${gear.head} <!-- Equipment helmet / hood -->
        ${headExtras} <!-- Hair / hats / hoods -->
        <g data-face-accessory-rig="${resolvedShape}" transform="${faceTransform}">
          ${renderAccessory(accessory, props.eyeStyle)}
        </g>
        ${extras.front} <!-- Held & belt props -->
        ${heldInForeground ? `<g data-held-assembly="foreground">
          ${weaponContent}
          ${rearInForeground ? rearArmAndHand : ''}
          ${frontArmAndHand}
        </g>` : ''}
        ${poseLayers.accent}
        </g>
      </g>
    </svg>`;
  }
}
